import type { CSSProperties, ReactNode } from 'react';
import { Backdrop } from '../../../modules/design-system';
import type { ApiClient } from '../../../modules/api';
import { MyPacksFooter } from '../../MyPacks/MyPacksFooter';
import { DeliveryHeader } from './DeliveryHeader';

export interface DeliveryPageShellProps {
  /** Needed by the footer to load the operator's partner links. */
  client: ApiClient;
  title: string;
  onBack: () => void;
  backLabel?: string;
  /** Drop the noise-SVG overlay from the page gradient. Mirrors the
   *  same flag on MyPacksPage so the whole flow stays consistent. */
  hideBackgroundPattern?: boolean;
  children: ReactNode;
}

/**
 * Common chrome for Delivery New / Show pages — backdrop, optional
 * hero photo layer (driven by `--co-DeliveryPageBackground-bgImage`
 * with cascade fallback to `--co-MyPacksPageBackground-bgImage`),
 * sticky header and the footer. The body is whatever the page renders
 * inside, vertically centred in a max-w-640 column.
 *
 * `data-page="delivery"` activates the dedicated Delivery cascade in
 * `index.css` — gradient + noise read from `--co-DeliveryPageBackground-*`
 * with fallback to the MyPacks namespace. By default the screen still
 * looks identical to the MyPacks flow; the operator peels it off via
 * the `DeliveryPageBackground` registry entry.
 */
export function DeliveryPageShell({
  client,
  title,
  onBack,
  backLabel,
  hideBackgroundPattern = false,
  children,
}: DeliveryPageShellProps) {
  return (
    <div
      data-page="delivery"
      style={
        hideBackgroundPattern
          ? ({ '--co-DeliveryPageBackground-noiseOpacity': 0 } as CSSProperties)
          : undefined
      }
    >
      <Backdrop bgImageUrl={null} />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage: 'var(--co-DeliveryPageBackground-bgImage, none)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div
        data-co-component="DeliveryPageBackground"
        className="@container relative flex min-h-screen w-full flex-col"
        style={{
          fontFamily: 'var(--co-DeliveryHeader-fontFamily, Inter), system-ui, sans-serif',
        }}
      >
        <DeliveryHeader
          title={title}
          onBack={onBack}
          {...(backLabel !== undefined && { backLabel })}
        />
        <div className="mx-auto flex w-full max-w-350 flex-1 flex-col">
          <main className="flex-1 px-4 pb-12 @[600px]:px-6">
            <div className="mx-auto w-full max-w-[640px] py-4">{children}</div>
          </main>
          <MyPacksFooter client={client} inspectName="DeliveryFooter" />
        </div>
      </div>
    </div>
  );
}
